/**
 * ClaimBusiness — `/reclamar/:slug`
 *
 * Pide email del propietario. Backend decide automáticamente:
 *  · auto_email: envía al notification_email del perfil
 *  · manual_review: SuperAdmin lo aprueba en 24-48h
 */
import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { BACKEND_URL } from "../lib/api";
import GlobalHeader from "../components/layout/GlobalHeader";
import TurnstileWidget from "../components/TurnstileWidget";
import { Storefront, ShieldCheck, CheckCircle, ClockCounterClockwise } from "@phosphor-icons/react";

const api = axios.create({ baseURL: `${BACKEND_URL}/api` });

export default function ClaimBusiness() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ email: "", contact_name: "", note: "" });
  const [tToken, setTToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    document.title = "Reclama tu perfil · AG-Ecosystem";
    api.get(`/marketplace/centros/${slug}`).then(({ data }) => {
      setOrg(data?.org || null);
    }).catch(() => setOrg(null)).finally(() => setLoading(false));
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post(`/centro/${slug}/claim/request`, {
        ...form, turnstile_token: tToken, honeypot: "",
      });
      setResult(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "No se pudo procesar la reclamación.");
    } finally { setBusy(false); }
  };

  if (loading) return <Shell><div className="text-[var(--ag-text-dim)] text-center">Cargando…</div></Shell>;
  if (!org) return <Shell>
    <div className="text-center">
      <h1 className="font-display text-3xl font-bold tracking-tighter text-[var(--ag-text)]">Perfil no encontrado</h1>
      <p className="text-[var(--ag-text-dim)] mt-3">El centro que intentas reclamar no existe o ya no está disponible.</p>
      <Link to="/marketplace" className="ag-btn-gold inline-block mt-6 px-5 py-2.5 text-xs uppercase tracking-wider">Volver al marketplace</Link>
    </div>
  </Shell>;

  if (org.user_id) return <Shell>
    <div className="text-center" data-testid="claim-already">
      <ShieldCheck size={26} weight="fill" className="text-emerald-300 mx-auto" />
      <h1 className="font-display text-3xl font-bold tracking-tighter mt-3 text-[var(--ag-text)]">Este perfil ya está reclamado</h1>
      <p className="text-[var(--ag-text-dim)] mt-3 text-sm">Si crees que tu negocio ha sido reclamado por otra persona, contacta con nuestro soporte.</p>
      <Link to={`/centro/${slug}`} className="ag-btn-gold inline-block mt-6 px-5 py-2.5 text-xs uppercase tracking-wider">Ver perfil</Link>
    </div>
  </Shell>;

  if (result?.ok) {
    const isAuto = result.method === "auto_email";
    return <Shell>
      <div className="text-center" data-testid="claim-success">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-500/20 border border-emerald-500/40 mb-5">
          {isAuto ? <CheckCircle size={26} weight="fill" className="text-emerald-300" />
                  : <ClockCounterClockwise size={26} weight="fill" className="text-[var(--ag-gold)]" />}
        </div>
        <h1 className="font-display text-3xl font-bold tracking-tighter text-[var(--ag-text)]">{isAuto ? "Revisa tu email" : "Solicitud recibida"}</h1>
        <p className="text-[var(--ag-text-dim)] mt-3 text-sm leading-relaxed">{result.message}</p>
        <Link to={`/centro/${slug}`} className="mt-6 inline-block text-xs text-[var(--ag-gold)] hover:underline">← Volver al perfil</Link>
      </div>
    </Shell>;
  }

  return (
    <Shell>
      <form onSubmit={submit} className="space-y-5" data-testid="claim-form">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--ag-gold)]">Reclamación gratuita</div>
          <h1 className="font-display text-3xl font-bold tracking-tighter mt-2">{org.name}</h1>
          <p className="text-[var(--ag-text-dim)] text-sm mt-1">{org.city}{org.address ? ` · ${org.address}` : ""}</p>
        </div>
        <p className="text-sm text-[var(--ag-text-dim)] leading-relaxed">
          Si eres el propietario de este negocio, reclámalo gratis. Al confirmar tendrás:
        </p>
        <ul className="text-sm text-[var(--ag-text)]/90 space-y-1.5 pl-4">
          <li>· <strong className="text-[var(--ag-text)]">Prueba gratuita de 30 días</strong> con todas las funcionalidades</li>
          <li>· Edición completa de tu perfil público</li>
          <li>· Inbox de leads y mensajes</li>
          <li>· Sin necesidad de tarjeta</li>
        </ul>
        <div>
          <Label>Tu email *</Label>
          <Input type="email" required value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="claim-email" placeholder="tu@email.com" />
          <p className="text-[10px] text-[var(--ag-text-dim)] mt-1">
            {org.notification_email
              ? "Enviaremos el link de confirmación al email registrado del negocio."
              : "Como el perfil no tiene email registrado, nuestro equipo verificará tu identidad manualmente (24-48h)."}
          </p>
        </div>
        <div>
          <Label>Persona de contacto (opcional)</Label>
          <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
        </div>
        <div>
          <Label>Nota para nuestro equipo (opcional)</Label>
          <Textarea rows={3} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
            placeholder="Información adicional que ayude a verificar que es tu negocio…" />
        </div>
        <TurnstileWidget onToken={setTToken} />
        {error && <div className="text-sm text-red-400" data-testid="claim-error">{error}</div>}
        <button type="submit" disabled={busy}
          className="ag-btn-gold w-full py-3.5 text-sm font-semibold tracking-wider uppercase"
          data-testid="claim-submit">
          {busy ? "Enviando…" : "Reclamar perfil"}
        </button>
        <p className="text-center text-[10px] text-[var(--ag-text-dim)] leading-relaxed" data-testid="claim-legal-notice">
          Al continuar aceptas la <a href="/privacidad" target="_blank" rel="noopener noreferrer" className="text-[var(--ag-gold)] hover:underline">Política de privacidad</a> y los <a href="/terminos" target="_blank" rel="noopener noreferrer" className="text-[var(--ag-gold)] hover:underline">Términos</a>.
          <br />
          ¿No es tu negocio? <Link to={`/centro/${slug}`} className="hover:text-white">Volver al perfil</Link>
        </p>
      </form>
    </Shell>
  );
}

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[var(--ag-midnight)] text-[var(--ag-text)]">
    <GlobalHeader />
    <main className="max-w-md mx-auto px-6 py-12 md:py-20">
      <div className="ag-card p-8">{children}</div>
    </main>
  </div>
);
const Label = ({ children }) => (
  <label className="block text-[10px] uppercase tracking-[0.15em] text-[var(--ag-text-dim)] mb-1.5">{children}</label>
);
const Input = (props) => (
  <input {...props}
    className={`w-full h-11 px-3 rounded-sm bg-[var(--ag-midnight-soft)] border border-[var(--ag-border)] text-[var(--ag-text)] text-sm focus:border-[var(--ag-gold)]/60 outline-none ${props.className||""}`} />
);
const Textarea = (props) => (
  <textarea {...props}
    className={`w-full px-3 py-2.5 rounded-sm bg-[var(--ag-midnight-soft)] border border-[var(--ag-border)] text-[var(--ag-text)] text-sm focus:border-[var(--ag-gold)]/60 outline-none ${props.className||""}`} />
);
